<template>
  <div>
    <template v-if="loading">
      <hr />
      <b-spinner variant="secondary" />
    </template>
    <template v-else>
      <hr v-if="filters.length" />
      <b-row>
        <b-col
          md="6"
          v-for="(
            {
              filter,
              label,
              options,
              component,
              endpoint,
              trackBy,
              searchFields,
              selectLabel,
              type,
              transformValue,
              multiple,
              picklistName,
              initFetchLabels,
              customLabel,
              inputType,
            },
            index
          ) in filtersHidrated"
          :key="index"
        >
          <filter-holder
            :operation.sync="filter.operation"
            :label="label"
            :type="type"
            @remove="removeFilter(index)"
          >
            <component
              v-if="!filter.operation.endsWith('null')"
              :is="component"
              v-model="filter.value"
              :options="options"
              :dateType="type"
              :label="selectLabel ? selectLabel : label"
              :endpoint="endpoint"
              :trackBy="trackBy"
              :searchFields="searchFields"
              :transformValue="transformValue"
              :multiple="multiple"
              :picklistName="picklistName"
              :initFetchLabels="initFetchLabels"
              :customLabel="customLabel"
              :inputType="inputType"
              :valueType="type"
            />
          </filter-holder>
        </b-col>
      </b-row>
      <hr />
      <div>
        <b-dropdown :text="$t('filters.add')" class="align-baseline mr-2">
          <b-dropdown-item
            v-for="(filterOption, index) in filterOptions"
            :key="index"
            @click="addFilter(filterOption.type)"
          >
            {{ filterOption.label }}
          </b-dropdown-item>
        </b-dropdown>
        {{ $t('filters.advanced.logic') }}:
        <b-form-radio-group
          v-model="logic"
          :options="logicOptions"
          class="d-inline ml-2"
        />
        <b-button
          v-if="filters.length"
          variant="outline-secondary"
          size="sm"
          @click="removeAllFilter()"
          class="text-nowrap"
        >
          {{ $t('filters.advanced.remove-all') }}
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
/* eslint-disable vue/no-unused-components */

import { mapGetters } from 'vuex';
import DatePicker from '../DatePicker.vue';
import FilterHolder from '../FilterHolder.vue';
import FilterMultiselect from './Multiselect.vue';
import Partner from '../inputs/Partner.vue';
import CrudMultiSelect from './CrudMultiSelect.vue';
import CrudPicklistMultiSelect from './CrudPicklistMultiSelect.vue';
import FromToFilter from './FromToFilter.vue';
import FromToInputFilter from './FromToInputFilter.vue';
import Geo from './Geo.vue';

export default {
  components: {
    FilterHolder,
    FilterMultiselect,
    CrudMultiSelect,
    CrudPicklistMultiSelect,
    DatePicker,
    FromToFilter,
    FromToInputFilter,
    Geo,
    'input-partner-assigned': Partner,
  },
  props: ['value', 'filterOptions'],
  data() {
    return {
      filters: [],
      logicOptions: ['and', 'or'].map((option) => ({
        value: option,
        text: this.$t(`filters.advanced.${option}`),
      })),
      logic: 'and',
      loading: false,
    };
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.emitChange();
      },
    },
    logic() {
      this.emitChange();
    },
  },
  computed: {
    ...mapGetters(['locale']),

    filtersHidrated() {
      return this.filters.reduce((filters, filter) => {
        const filterOption = this.filterOptions.find(
          (filterOption) => filterOption.type == filter.type,
        );
        if (filterOption) {
          filters.push({
            ...filterOption,
            options: filterOption.values,
            filter,
          });
        }
        return filters;
      }, []);
    },
  },
  created() {
    this.setFilters();
  },
  methods: {
    addFilter(filterType) {
      const filterOption = this.filterOptions.find(
        ({ type }) => filterType == type,
      );
      this.filters.push({
        operation: 'includes',
        type: filterType,
        value:
          filterOption?.component == 'geo'
            ? {
                latitude: 52.52,
                longitude: 13.4,
                radius: 50000,
              }
            : null,
      });
    },
    removeFilter(index) {
      this.filters.splice(index, 1);
    },
    removeAllFilter() {
      this.filters = [];
    },
    setFilters() {
      if (this.value) {
        const [logic] = Object.keys(this.value);
        if (['and', 'or'].includes(logic)) {
          this.logic = logic;
          this.filters = this.value[logic].map((filter) => {
            const filterOption = this.filterOptions.find(
              ({ type }) => filter.field == type,
            );
            const filterMapped = {
              operation: filter.op,
              type: filter.field,
              value: filter.value,
            };
            if (
              filterOption?.component == 'CrudMultiSelect' &&
              filterOption?.trackBy
            ) {
              filterMapped.value = filterOption.multiple
                ? filter.value.map((v) => ({ [filterOption.trackBy]: v }))
                : { [filterOption.trackBy]: filterMapped.value };
            }
            return filterMapped;
          });
        }
      }
    },
    emitChange() {
      this.$emit('input', {
        [this.logic]: this.filters
          .filter(({ value, operation }) => {
            if (Array.isArray(value) && !value.length > 0) {
              return false;
            }
            return (
              (value != null && value !== '') || operation?.endsWith('null')
            );
          })
          .map((filter) => {
            const filterOption = this.filterOptions.find(
              (el) => filter.type == el.type,
            );
            const getTrackValue = (val) => {
              if (
                typeof val === 'object' &&
                filterOption &&
                filterOption.component == 'CrudMultiSelect' &&
                val[filterOption.trackBy]
              ) {
                return val[filterOption.trackBy];
              }
              return val;
            };

            let value = filter.value;

            if (
              filterOption.component === 'filter-multiselect' &&
              filterOption.multiple === false &&
              Array.isArray(filter.value)
            ) {
              value = filter.value[0];
            }

            return {
              op: filter.operation,
              field: filter.type,
              value: Array.isArray(value)
                ? value.map((val) => getTrackValue(val))
                : getTrackValue(value),
            };
          }),
      });
    },
  },
};
</script>
